import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { 
  type B2BModule, 
  type PlanId,
  PLANS,
  B2B_MODULES,
  hasModuleAccess as checkModuleAccess
} from '@/config/plans';

export type { B2BModule } from '@/config/plans';

interface ModuleAddon {
  id: string;
  addon_type: string;
  module_key: string | null;
  extra_units: number | null;
  is_active: boolean;
}

interface B2BAccessResult {
  // Access checks
  hasB2BAccess: boolean;
  b2bAccessType: 'none' | 'single' | 'all';
  canAccessModule: (module: B2BModule) => boolean;
  
  // Selected module (for Pro plan)
  selectedModule: B2BModule | null;
  needsModuleSelection: boolean;
  selectModule: (module: B2BModule) => Promise<void>;
  
  // Additional modules (add-ons)
  additionalModules: B2BModule[];
  hasAdditionalModule: (module: B2BModule) => boolean;
  
  // Available modules
  accessibleModules: B2BModule[];
  lockedModules: B2BModule[];
  
  // Loading state
  isLoading: boolean;
  
  // Plan info
  planId: PlanId;
  planName: string;
}

/**
 * Hook for managing B2B module access based on user's plan and add-ons
 */
export function useB2BAccess(): B2BAccessResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch user's subscription plan and selected B2B module
  const { data: b2bData, isLoading: dataLoading } = useQuery({
    queryKey: ['b2b-access-data', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return { planSlug: 'discovery', selectedModule: null };
      }
      
      // Fetch profile for selected_b2b_module
      const { data: profileData } = await supabase
        .from('profiles')
        .select('selected_b2b_module')
        .eq('id', user.id)
        .single();
      
      // Fetch active subscription with plan info
      const { data: subData } = await supabase
        .from('subscriptions')
        .select(`
          status,
          plan_id,
          subscription_plans:plan_id (
            slug
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();
      
      // Extract plan slug from subscription or fallback to discovery
      let planSlug = 'discovery';
      if (subData?.subscription_plans) {
        // Handle potential array or object response
        const plans = subData.subscription_plans;
        if (Array.isArray(plans) && plans.length > 0) {
          planSlug = plans[0].slug || 'discovery';
        } else if (typeof plans === 'object' && plans !== null) {
          planSlug = (plans as { slug?: string }).slug || 'discovery';
        }
      }
      
      return {
        planSlug,
        selectedModule: profileData?.selected_b2b_module as B2BModule | null,
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get plan info
  const planId = (b2bData?.planSlug || 'discovery') as PlanId;
  const plan = PLANS[planId] || PLANS.discovery;
  const selectedModule = b2bData?.selectedModule || null;

  // Fetch module add-ons
  const { data: addons = [], isLoading: addonsLoading } = useQuery({
    queryKey: ['module-addons', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('module_addons')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .eq('addon_type', 'b2b_module');
      
      if (error) {
        console.error('Error fetching module addons:', error);
        return [];
      }
      
      return data as ModuleAddon[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation to select a module (for Pro plan)
  const selectModuleMutation = useMutation({
    mutationFn: async (module: B2BModule) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { error } = await supabase
        .from('profiles')
        .update({ selected_b2b_module: module })
        .eq('id', user.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['b2b-access-data'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  // Compute access info
  const accessInfo = useMemo(() => {
    const b2bAccessType = plan.b2bAccess;
    const hasB2BAccess = b2bAccessType !== 'none';
    
    // Get additional modules from add-ons
    const additionalModules = addons
      .filter(a => a.module_key)
      .map(a => a.module_key as B2BModule);
    
    // Determine which modules are accessible
    const allModules: B2BModule[] = ['fleet', 'properties', 'tenders', 'subcontractors'];
    
    const accessibleModules = allModules.filter(module => {
      // Check base plan access
      if (checkModuleAccess(planId, module, selectedModule)) return true;
      // Check add-on access
      if (additionalModules.includes(module)) return true;
      return false;
    });
    
    const lockedModules = allModules.filter(m => !accessibleModules.includes(m));
    
    // Check if Pro user needs to select their module
    const needsModuleSelection = 
      b2bAccessType === 'single' && 
      !selectedModule && 
      additionalModules.length === 0;
    
    return {
      hasB2BAccess,
      b2bAccessType,
      additionalModules,
      accessibleModules,
      lockedModules,
      needsModuleSelection,
    };
  }, [plan, planId, selectedModule, addons]);

  // Function to check module access
  const canAccessModule = (module: B2BModule): boolean => {
    return accessInfo.accessibleModules.includes(module);
  };

  // Function to check if module is from add-on
  const hasAdditionalModule = (module: B2BModule): boolean => {
    return accessInfo.additionalModules.includes(module);
  };

  return {
    // Access checks
    hasB2BAccess: accessInfo.hasB2BAccess,
    b2bAccessType: accessInfo.b2bAccessType,
    canAccessModule,
    
    // Selected module
    selectedModule,
    needsModuleSelection: accessInfo.needsModuleSelection,
    selectModule: async (module: B2BModule) => {
      await selectModuleMutation.mutateAsync(module);
    },
    
    // Additional modules
    additionalModules: accessInfo.additionalModules,
    hasAdditionalModule,
    
    // Available modules
    accessibleModules: accessInfo.accessibleModules,
    lockedModules: accessInfo.lockedModules,
    
    // Loading state
    isLoading: dataLoading || addonsLoading || selectModuleMutation.isPending,
    
    // Plan info
    planId,
    planName: plan.name,
  };
}

/**
 * Get module info by key
 */
export function getModuleInfo(module: B2BModule) {
  return B2B_MODULES[module];
}

/**
 * All B2B modules list
 */
export const ALL_B2B_MODULES: B2BModule[] = ['fleet', 'properties', 'tenders', 'subcontractors'];
